import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useHistory, useLocation } from 'react-router-dom';

import {
  makeStyles,
  FormControl,
  FormHelperText,
  InputLabel,
  FilledInput,
  InputAdornment,
  IconButton,
  Typography,
  Link,
  Button,
  Box
} from '@material-ui/core';
import {
  Visibility,
  VisibilityOff
} from '@material-ui/icons';

import Alert from '../common/alert';
import MinimalFormPage from '../layout/MinimalFormPage';

import {
  login,
  clearLoginState,
  selectLoginStatus
} from '../auth/authSlice';

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center'
  },
  btn: {
    minWidth: '147px'
  }
}));

const LoginPage = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { loggedIn, status, error } = useSelector(selectLoginStatus);
  const { from } = location.state || { from: { pathname: '/' } };

  const [ showPassword, setShowPassword ] = useState(false);
  const [ formValues, setFormValues ] = useState({username: '', password: ''});
  const [ formErrors, setFormErrors ] = useState({});

  const validations = {
    username: username => {
      if(!username || username.trim() === '') {
        return t('login:fieldIsRequired', {field: t('login:username')});
      }

      return null;
    },
    password: password => {
      if(!password || password.trim() === '') {
        return t('login:fieldIsRequired', {field: t('login:password')});
      }

      return null;
    }
  };

  // Redirect on successful login
  useEffect(() => {
    if(loggedIn) {
      history.replace(from);
    }
  }, [loggedIn]);

  // Clear request state on unmount
  useLayoutEffect(() => {
    return () => {
      dispatch(clearLoginState());
    }
  }, []);

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formValues).forEach(field => {
      const value = formValues[field];
      newErrors[field] = validations[field](value);
    });

    setFormErrors(newErrors);

    if(Object.keys(newErrors).every(field => newErrors[field] === null)) {
      return true;
    }

    return false;
  }

  const handleChange = event => {
    const target = event.target;
    const value = target.value;
    const name = target.name;

    setFormValues({
      ...formValues,
      [name]: value
    });

    // Reset errors
    setFormErrors({
      ...formErrors,
      [name]: null
    });

    // Reset possible request error messages
    if(status === 'failed') {
      dispatch(clearLoginState());
    }
  };

  const handleSubmit = event => {
    event.preventDefault();

    if(validateForm()) {
      if(status !== 'loading') {
        dispatch(login(formValues));
      }
    }
  };

  return (
    <MinimalFormPage>

      <form className={classes.form} onSubmit={handleSubmit}>
        <FormControl variant="filled" margin="normal" error={!!formErrors.username}>
          <InputLabel htmlFor="username">{t('login:username')}</InputLabel>
          <FilledInput
            type="email"
            id="username"
            name="username"
            value={formValues.username}
            required
            placeholder={t('login:usernamePlaceholder')}
            autoComplete="email"
            autoFocus
            onChange={handleChange}
            aria-describedby={formErrors.username ? 'username-error-text' : undefined }
          />
          {formErrors.username ? <FormHelperText id="username-error-text">{formErrors.username}</FormHelperText> : null}
        </FormControl>

        <FormControl variant="filled" margin="normal" error={!!formErrors.password}>
          <InputLabel htmlFor="password">{t('login:password')}</InputLabel>
          <FilledInput
            type={showPassword ? 'text' : 'password'}
            id="password"
            name="password"
            value={formValues.password}
            required
            placeholder={t('login:passwordPlaceholder')}
            autoComplete="current-password"
            onChange={handleChange}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label={t('login:togglePasswordVisibility')}
                  title={t('login:togglePasswordVisibility')}
                  onClick={e => setShowPassword(!showPassword)}
                  onMouseDown={e => e.preventDefault()}
                  edge="end"
                >
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            }
            aria-describedby={formErrors.password ? 'password-error-text' : undefined }
          />
          {formErrors.password ? <FormHelperText id="password-error-text">{formErrors.password}</FormHelperText> : null}
        </FormControl>

        {status === 'failed' ? <Box mt={2}><Alert severity="error">{error.message}</Alert></Box> : null}

        <Box mt={2} mb={3}>
          <Button className={classes.btn} color="primary" variant="contained" type="submit" disabled={status === 'loading'}>{t('login:login')}</Button>
        </Box>
      </form>

      <Typography variant="body1" component="p">
        <Link component={RouterLink} to="/forgot-password">{t('login:forgotPassword')}</Link>
      </Typography>
    </MinimalFormPage>
  );
};

export default LoginPage;
